import React, { useState } from "react";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { integrations, sampleQuestions } from "../mock";
import { Check, X, Sparkles, Clock } from "lucide-react";

const InteractiveDemo = () => {
    const [selectedQuestion, setSelectedQuestion] = useState(
        sampleQuestions[0],
    );
    const [isGenerating, setIsGenerating] = useState(false);
    const [showAnswer, setShowAnswer] = useState(true);
    const [connectedIntegrations, setConnectedIntegrations] = useState(
        integrations.filter((i) => i.connected).map((i) => i.id),
    );

    const handleQuestionClick = (question) => {
        setIsGenerating(true);
        setShowAnswer(false);

        setTimeout(() => {
            setSelectedQuestion(question);
            setIsGenerating(false);
            setShowAnswer(true);
        }, 1200);
    };

    const toggleIntegration = (integrationId) => {
        setConnectedIntegrations((prev) =>
            prev.includes(integrationId)
                ? prev.filter((id) => id !== integrationId)
                : [...prev, integrationId],
        );
    };

    return (
        <section className="py-24 bg-gradient-to-b from-white to-slate-50">
            <div className="max-w-7xl mx-auto px-6">
                {/* Section header */}
                <div className="text-center mb-16 max-w-3xl mx-auto">
                    <Badge className="mb-4 bg-teal-100 text-teal-700 hover:bg-teal-200 px-4 py-2">
                        <Sparkles className="w-4 h-4 mr-2 inline" />
                        Interactive Demo
                    </Badge>
                    <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
                        See Nucleus in Action
                    </h2>
                    <p className="text-xl text-slate-600 leading-relaxed">
                        Connect your tools once. Ask questions in natural
                        language. Get instant, context-aware answers powered by
                        AI.
                    </p>
                </div>

                {/* Demo container */}
                <div className="grid lg:grid-cols-3 gap-8">
                    {/* Left: Integrations */}
                    <div className="lg:col-span-1">
                        <Card className="p-6 border-2 border-slate-200 shadow-lg">
                            <h3 className="text-lg font-bold text-slate-900 mb-6">
                                Connected Tools
                            </h3>
                            <div className="space-y-3">
                                {integrations.map((integration) => {
                                    const isConnected =
                                        connectedIntegrations.includes(
                                            integration.id,
                                        );
                                    return (
                                        <div
                                            key={integration.id}
                                            onClick={() =>
                                                toggleIntegration(
                                                    integration.id,
                                                )
                                            }
                                            className={`p-4 mb-2 rounded-lg border-2 cursor-pointer transition-all duration-300 ${
                                                isConnected
                                                    ? "border-teal-500 bg-teal-50 shadow-sm"
                                                    : "border-slate-200 bg-white hover:border-slate-300"
                                            }`}
                                        >
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-3">
                                                    <span className="text-2xl">
                                                        {integration.icon}
                                                    </span>
                                                    <span className="font-semibold text-slate-900">
                                                        {integration.name}
                                                    </span>
                                                </div>
                                                <div
                                                    className={`w-6 h-6 rounded-full flex items-center justify-center ${
                                                        isConnected
                                                            ? "bg-teal-600"
                                                            : "bg-slate-300"
                                                    }`}
                                                >
                                                    {isConnected ? (
                                                        <Check className="w-4 h-4 text-white" />
                                                    ) : (
                                                        <X className="w-4 h-4 text-white" />
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            <p className="text-xs text-slate-500 mt-4 text-center">
                                Click to connect/disconnect (demo only)
                            </p>
                        </Card>
                    </div>

                    {/* Right: Questions and Answers */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Question selector */}
                        <Card className="p-6 border-2 border-slate-200 shadow-lg">
                            <h3 className="text-lg font-bold text-slate-900 mb-4">
                                Try asking Nucleus:
                            </h3>
                            <div className="grid sm:grid-cols-2 gap-3">
                                {sampleQuestions.map((question) => (
                                    <Button
                                        key={question.id}
                                        variant={
                                            selectedQuestion.id === question.id
                                                ? "default"
                                                : "outline"
                                        }
                                        className={`text-left h-auto p-4 justify-start transition-all duration-300 ${
                                            selectedQuestion.id === question.id
                                                ? "bg-blue-600 text-white border-blue-600 shadow-md"
                                                : "hover:border-blue-300 hover:bg-blue-50"
                                        }`}
                                        onClick={() =>
                                            handleQuestionClick(question)
                                        }
                                    >
                                        <span className="font-medium text-sm">
                                            {question.question}
                                        </span>
                                    </Button>
                                ))}
                            </div>
                        </Card>

                        {/* Answer display */}
                        <Card className="p-8 border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-white shadow-xl min-h-96">
                            <div className="flex items-start justify-between mb-4">
                                <h3 className="text-lg font-bold text-slate-900">
                                    {selectedQuestion.question}
                                </h3>
                                {!isGenerating && (
                                    <Badge
                                        variant="outline"
                                        className="text-xs text-slate-600 border-slate-300"
                                    >
                                        <Clock className="w-3 h-3 mr-1" />
                                        {selectedQuestion.timeToAnswer}
                                    </Badge>
                                )}
                            </div>

                            {isGenerating ? (
                                <div className="space-y-4 py-8">
                                    <div className="flex items-center gap-3">
                                        <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce"></div>
                                        <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce animation-delay-200"></div>
                                        <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce animation-delay-400"></div>
                                        <span className="text-sm text-slate-600 ml-2">
                                            Analyzing commits, PRs, tickets, and
                                            messages...
                                        </span>
                                    </div>
                                    <div className="space-y-2">
                                        <div className="h-4 bg-slate-200 rounded w-3/4 animate-pulse"></div>
                                        <div className="h-4 bg-slate-200 rounded w-full animate-pulse"></div>
                                        <div className="h-4 bg-slate-200 rounded w-2/3 animate-pulse"></div>
                                    </div>
                                </div>
                            ) : showAnswer ? (
                                <div className="prose prose-slate max-w-none">
                                    <div className="whitespace-pre-line text-slate-700 leading-relaxed">
                                        {selectedQuestion.answer}
                                    </div>
                                </div>
                            ) : null}

                            {!isGenerating && showAnswer && (
                                <div className="mt-6 pt-6 border-t border-slate-200">
                                    <p className="text-sm text-slate-500 italic">
                                        ✨ This answer was generated by
                                        analyzing data from{" "}
                                        {connectedIntegrations.length} connected
                                        tools
                                    </p>
                                </div>
                            )}
                        </Card>
                    </div>
                </div>

                {/* Bottom note */}
                <div className="mt-12 text-center">
                    <p className="text-slate-600">
                        This is a simplified demo. Real Nucleus analyzes your
                        entire engineering data universe.
                    </p>
                </div>
            </div>
        </section>
    );
};

export default InteractiveDemo;
